import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { backendJson, backendFetch, CSRF_COOKIE_NAME, getCookie } from "@/lib/backend";

interface AuthContextType {
  user: UserMeResponse | null;
  loading: boolean;
  refreshUser: () => Promise<UserMeResponse | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface UserMeResponse {
  id: string;
  email?: string | null;
  email_verified: boolean;
  full_name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  avatar_url?: string | null;
  provider: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserMeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = async (): Promise<UserMeResponse | null> => {
    try {
      const { response, data } = await backendJson<UserMeResponse>("/api/auth/me", {
        method: "GET",
        retryOn401: true,
      });

      if (response.ok && data) {
        return data;
      }

      if (response.status === 401) {
        return null;
      }

      // Unexpected; treat as logged out.
      return null;
    } catch {
      return null;
    }
  };

  const refreshUser = async (): Promise<UserMeResponse | null> => {
    const me = await fetchMe();
    setUser(me);
    return me;
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const me = await fetchMe();
        if (cancelled) return;
        setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signOut = async () => {
    const csrf = getCookie(CSRF_COOKIE_NAME) ?? "";
    await backendFetch("/api/auth/logout", {
      method: "POST",
      headers: csrf ? { "x-csrf-token": csrf } : {},
      retryOn401: false,
    });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}